import React, { Fragment, useEffect } from 'react';
import NumericInputElement, { TypeOnBlur } from '../common/numericinput';
import { formatCurrency } from '../common/common';
import { Box, Checkbox, Chip, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderRowNameAndDetails } from './orderrownameanddetails';
import { cloneDeep } from 'lodash';

export function FilterOrderRowViewInternal(props) {
    const { t } = useTranslation();
    const foCache = React.useRef(null);
    const saving = props.saving;

    useEffect(() => {
        if (props.filterOrder && foCache.current) {
            if (
                props.filterOrder.filterCount !== foCache.current.filterCount ||
                props.filterOrder.totalPriceWithoutVat !== foCache.current.totalPriceWithoutVat ||
                props.filterOrder.unitPriceWithoutVat !== foCache.current.unitPriceWithoutVat
            ) {
                foCache.current.filterCount = props.filterOrder.filterCount;
                foCache.current.totalPriceWithoutVat = props.filterOrder.totalPriceWithoutVat;
                foCache.current.unitPriceWithoutVat = props.filterOrder.unitPriceWithoutVat;
            }
        } else if (props.filterOrder && !foCache.current) {
            foCache.current = cloneDeep(props.filterOrder);
        }
    }, [props.filterOrder]);

    const onClickRow = () => {
        props.startEditRow();
    };
    const onClickToSelect = (e) => {
        e.stopPropagation();
        props.selectRow(props.filterOrder.id);
    };
    const getRowClassName = () => (props.selected ? 'highlight' : '');
    const getRowDynamicSx = (display) => {
        const sx = { display };
        if (props.fadeRow) sx.opacity = 0.3;
        if (props.index === 0 || props.index % 2 === 0) sx.backgroundColor = '#f0f0f0';
        return sx;
    };

    const filterCountChanged = (id, value) => {
        const fo = cloneDeep(foCache.current);
        fo.count = value;
        foCache.current = fo;
        props.filterOrderChanged(fo);
    };

    const editPalletInfo = (e) => {
        e.stopPropagation();
        props.editFoPalletInfo([props.filterOrder.id]);
    };

    return (
        <Fragment>
            <Box component="tr" className={getRowClassName()} sx={getRowDynamicSx({ xs: 'none', md: 'table-row' })}>
                <td
                    onClick={onClickToSelect}
                    name={`filter-order-row-select-${props.filterOrder.id}`}
                    style={{ verticalAlign: 'top' }}>
                    <Checkbox checked={!!props.selected} readOnly />
                </td>
                <td onClick={onClickRow} name={'filter-order-row'}>
                    {!props.priceOnly && (
                        <div>
                            {props.filterOrder.machineName}

                            {props.filterOrder.machineLocation && props.filterOrder.machineLocation.length > 0 && (
                                <span>
                                    <br />
                                    {t('location.position')}: <strong>{props.filterOrder.machineLocation}</strong>
                                </span>
                            )}
                            {props.filterOrder.machineArea && props.filterOrder.machineArea.length > 0 && (
                                <span>
                                    <br />
                                    {t('location.serviceArea')}: <strong>{props.filterOrder.machineArea}</strong>
                                </span>
                            )}
                            {props.filterOrder.palletInformation && props.filterOrder.palletInformation.length > 0 && (
                                <React.Fragment>
                                    {!props.editingOrder ? (
                                        <React.Fragment>
                                            <br />
                                            <Chip
                                                name={'edit-pallet-info-' + props.filterOrder.id}
                                                label={
                                                    t('order.palletGroup') + ': ' + props.filterOrder.palletInformation
                                                }
                                                variant="outlined"
                                                onClick={editPalletInfo}
                                            />
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            <br />
                                            {`${t('order.palletGroup')}: ${props.filterOrder.palletInformation} `}
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    )}
                </td>

                <td onClick={onClickRow}>
                    <OrderRowNameAndDetails orderrow={props.filterOrder} mobile={false} />
                </td>
                <td className={'filter-count-' + props.filterOrder.id}>
                    {!props.priceOnly && (
                        <NumericInputElement
                            id={null}
                            value={props.filterOrder.count}
                            onChange={(id, value) => filterCountChanged(props.filterOrder.id, value)}
                            type={TypeOnBlur}
                            min={1}
                            saving={saving}
                        />
                    )}
                </td>
                <td>{formatCurrency(props.filterOrder.unitPriceWithoutVat, 2, props.currency)}</td>
                {props.allowPricing && (
                    <td>{formatCurrency(props.filterOrder.totalPriceWithoutVat, 2, props.currency)}</td>
                )}
            </Box>

            <Box component="tr" className={getRowClassName()} sx={getRowDynamicSx({ xs: 'table-row', md: 'none' })}>
                <td
                    onClick={onClickToSelect}
                    name={`filter-order-row-select-${props.filterOrder.id}`}
                    style={{ verticalAlign: 'top' }}>
                    <Checkbox sx={{ p: '0px 0px 0px 4px' }} checked={!!props.selected} onChange={onClickToSelect} />
                </td>
                <td onClick={onClickRow} name={'filter-order-row'}>
                    <OrderRowNameAndDetails orderrow={props.filterOrder} mobile={true} />
                    <br />
                    {!props.priceOnly && (
                        <span style={{ fontSize: '0.8em' }}>
                            <strong>{props.filterOrder.machineName}</strong>
                            {props.filterOrder.machineLocation && props.filterOrder.machineLocation.length > 0 && (
                                <span>, {props.filterOrder.machineLocation}</span>
                            )}
                            {props.filterOrder.machineArea && props.filterOrder.machineArea.length > 0 && (
                                <span>, {props.filterOrder.machineArea}</span>
                            )}
                        </span>
                    )}
                    {!props.priceOnly && props.selected && !props.editingOrder && (
                        <NumericInputElement
                            id={null}
                            value={props.filterOrder.count}
                            onChange={(id, value) => filterCountChanged(props.filterOrder.id, value)}
                            type={TypeOnBlur}
                            min={1}
                            saving={saving}
                        />
                    )}
                    {props.allowPricing && (
                        <div>
                            {props.filterOrder.count} x{' '}
                            {formatCurrency(props.filterOrder.unitPriceWithoutVat, 2, props.currency)}
                            &nbsp;=&nbsp;{formatCurrency(props.filterOrder.totalPriceWithoutVat, 2, props.currency)}
                        </div>
                    )}
                    {!props.priceOnly &&
                        props.filterOrder.palletInformation &&
                        props.filterOrder.palletInformation.length > 0 && (
                            <React.Fragment>
                                {!props.editingOrder ? (
                                    <React.Fragment>
                                        <Link style={{ color: 'black' }} underline="always" onClick={editPalletInfo}>
                                            {t('order.palletGroup')}:&nbsp;{props.filterOrder.palletInformation}
                                        </Link>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        {`${t('order.palletGroup')}: ${props.filterOrder.palletInformation} `}
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}
                </td>
            </Box>
        </Fragment>
    );
}

const shouldSkipRender = (prevProps, nextProps) => {
    if (
        prevProps.selected !== nextProps.selected ||
        prevProps.fadeRow !== nextProps.fadeRow ||
        prevProps.saving !== nextProps.saving ||
        prevProps.filterOrder !== nextProps.filterOrder // Reference check (Redux creates new objects)
    ) {
        return false; // DO re-render
    }
    return true; // Skip re-render
};

const FilterOrderRowView = React.memo(FilterOrderRowViewInternal, shouldSkipRender);

export default FilterOrderRowView;
